/* enhanced-megamenu-drawer.js */

class EnhancedMegamenuDrawer {
  constructor() {
    this.detailsList = [];
    this._boundToggles = new WeakSet();

    // idempotency markers
    this._guardedAreas = new WeakSet();
    this._guardedSubmenus = new WeakSet();
    this._drawerShielded = false;

    // bindings
    this.onDocClick = this.onDocClick.bind(this);
    this.onKeydown  = this.onKeydown.bind(this);

    // Install one global capture listener we control
    if (!window.__enhancedMega_docClick) {
      document.addEventListener('click', this.onDocClick, true);
      window.__enhancedMega_docClick = true;
    }

    this.init();
  }

  // ---------------------------------------------------------------------------
  // utils
  setExpanded(el, bool) {
    if (el) el.setAttribute('aria-expanded', bool ? 'true' : 'false');
  }

  qsa(sel, root = document) {
    return Array.from(root.querySelectorAll(sel));
  }

  // ---------------------------------------------------------------------------
  // init + refresh
  init() {
    // Shield the drawer so only the "X" closes it
    this.installDrawerClickShield();

    // Target the <details> that drive the enhanced panels
    this.detailsList = this.qsa('details.menu-drawer__enhanced-megamenu');
    if (!this.detailsList.length) return;

    this.detailsList.forEach((details) => {
      if (this._boundToggles.has(details)) return;
      this._boundToggles.add(details);

      // React to native <details> toggling
      details.addEventListener('toggle', () => this.onDetailsToggle(details));
      details.addEventListener('keydown', (e) => this.onKeydown(details, e));

      // If any panel starts opened (editor), make sure guards exist
      if (details.open) this.installGuardsWithin(details);
    });
  }

  refresh() {
    this.init();
  }

  // ---------------------------------------------------------------------------
  // GUARDS (the important bits)
  // 1) Drawer-wide capture shield: clicks inside the drawer do NOT bubble to
  //    any global "click to close" handler EXCEPT the X close button.
  installDrawerClickShield() {
    if (this._drawerShielded) return;
    const drawer = document.querySelector('.menu-drawer');
    if (!drawer) return;

    const shield = (e) => {
      const isCloseBtn = e.target.closest('.menu-drawer__close-modal-button');
      const isInsideDrawer = e.target.closest('.menu-drawer');
      if (isInsideDrawer && !isCloseBtn) {
        // Stop at capture so theme’s global handler never sees it
        e.stopPropagation();
      }
    };
    drawer.addEventListener('click', shield, { capture: true });
    this._drawerShielded = true;
  }

  // 2) For the scrollable submenu content: prevent “scroll → synthetic click”
  //    from reaching close handlers; also swallow pointer/touch events.
  installSubmenuGuards(root) {
    const submenus = this.qsa('.menu-drawer__submenu', root);
    submenus.forEach((area) => {
      if (this._guardedSubmenus.has(area)) return;
      this._guardedSubmenus.add(area);

      let startY = 0, moved = false;

      // Swallow interactions inside the submenu so globals don't see them
      const swallow = (e) => {
        if (!e.target.closest('.menu-drawer__close-modal-button')) {
          e.stopPropagation();
        }
      };
      [
        'pointerdown', 'pointermove', 'pointerup', 'pointercancel',
        'touchstart', 'touchmove', 'touchend', 'touchcancel',
        'mousedown', 'mousemove', 'mouseup', 'click'
      ].forEach(type => area.addEventListener(type, swallow, { capture: true }));

      // Cancel the “click after scroll” tap
      area.addEventListener('pointerdown', (e) => {
        startY = e.clientY || (e.touches && e.touches[0]?.clientY) || 0;
        moved = false;
      }, { passive: true });

      area.addEventListener('pointermove', (e) => {
        const y = e.clientY || (e.touches && e.touches[0]?.clientY) || 0;
        if (Math.abs(y - startY) > 6) moved = true;
      }, { passive: true });

      area.addEventListener('click', (e) => {
        if (moved && !e.target.closest('.menu-drawer__close-modal-button')) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true);
    });
  }

  // 3) Swiper strip guard: dragging the image carousel should never close the drawer
  installSwiperGuards(root) {
    const areas = this.qsa('.menu-drawer__enhanced-images', root);
    areas.forEach((area) => {
      if (this._guardedAreas.has(area)) return;
      this._guardedAreas.add(area);

      // Swallow everything (except the X) at capture
      const stop = (e) => {
        if (!e.target.closest('.menu-drawer__close-modal-button')) {
          e.stopPropagation();
        }
      };
      [
        'pointerdown', 'pointermove', 'pointerup', 'pointercancel',
        'touchstart', 'touchmove', 'touchend', 'touchcancel',
        'mousedown', 'mousemove', 'mouseup', 'click'
      ].forEach(type => area.addEventListener(type, stop, { capture: true }));

      // Prevent “drag → click” from being interpreted as a close signal
      let startX = 0, startY = 0, moved = false;
      area.addEventListener('pointerdown', (e) => {
        startX = e.clientX || (e.touches && e.touches[0]?.clientX) || 0;
        startY = e.clientY || (e.touches && e.touches[0]?.clientY) || 0;
        moved = false;
      }, { passive: true });

      area.addEventListener('pointermove', (e) => {
        const x = e.clientX || (e.touches && e.touches[0]?.clientX) || 0;
        const y = e.clientY || (e.touches && e.touches[0]?.clientY) || 0;
        if (Math.abs(x - startX) > 4 || Math.abs(y - startY) > 4) moved = true;
      }, { passive: true });

      area.addEventListener('click', (e) => {
        if (moved && !e.target.closest('.menu-drawer__close-modal-button')) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true);

      // Nice-to-have for mobile gesture routing
      area.style.touchAction = area.style.touchAction || 'pan-y';
      area.style.webkitOverflowScrolling = 'touch';
    });
  }

  // Helper to install all relevant guards inside a given <details> panel
  installGuardsWithin(details) {
    this.installSubmenuGuards(details);
    this.installSwiperGuards(details);
  }

  // ---------------------------------------------------------------------------
  // Toggle lifecycle
  onDetailsToggle(details) {
    // keep ARIA in sync with native <details> state
    this.setExpanded(details.querySelector('summary'), details.open);

    if (details.open) {
      // close siblings
      this.detailsList.forEach(d => { if (d !== details && d.open) d.open = false; });
      this.addMainClass();

      // Ensure the guards are bound for the newly-opened content
      this.installGuardsWithin(details);

      const first = details.querySelector('.menu-drawer__enhanced-submenu button, .menu-drawer__enhanced-submenu a, .menu-drawer__enhanced-submenu input');
      if (first) setTimeout(() => first.focus(), 0);
    } else {
      if (!document.querySelector('details.menu-drawer__enhanced-megamenu[open]')) {
        this.removeMainClass();
      }
    }
  }

  onKeydown(details, e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      details.open = false;
      this.setExpanded(details.querySelector('summary'), false);
      if (!document.querySelector('details.menu-drawer__enhanced-megamenu[open]')) this.removeMainClass();
    }
  }

  // ---------------------------------------------------------------------------
  // Delegated clicks: overlay + close buttons
  onDocClick(e) {
    // 1) Overlay: click outside submenu content but inside open <details>
    const openDetails = e.target.closest('details.menu-drawer__enhanced-megamenu[open]');
    if (openDetails) {
      const content = openDetails.querySelector('.menu-drawer__enhanced-submenu');
      if (content && !content.contains(e.target)) {
        openDetails.open = false;
        this.setExpanded(openDetails.querySelector('summary'), false);
        if (!document.querySelector('details.menu-drawer__enhanced-megamenu[open]')) this.removeMainClass();
        return;
      }
    }

    // 2) Back/Close (subpanel only)
    const closeBtn = e.target.closest('.menu-drawer__close-button');
    if (closeBtn) {
      e.preventDefault();
      e.stopPropagation();

      const details = closeBtn.closest('details.menu-drawer__enhanced-megamenu');
      if (details) {
        // close ONLY the submenu
        details.open = false;
        this.setExpanded(details.querySelector('summary'), false);
        details.classList.remove('menu-opening');

        // keep focus INSIDE the drawer so onFocusOut doesn't close it
        const summary = details.querySelector('summary');
        const headerDrawer = closeBtn.closest('header-drawer') || document.querySelector('header-drawer');
        const mainDetails  = headerDrawer?.querySelector('details.menu-drawer-container') || document.querySelector('header-drawer details.menu-drawer-container');

        queueMicrotask(() => {
          summary?.focus();
          if (mainDetails && typeof trapFocus === 'function') {
            trapFocus(mainDetails, summary || mainDetails.querySelector('summary'));
          }
        });
      }

      if (!document.querySelector('details.menu-drawer__enhanced-megamenu[open]')) {
        this.removeMainClass();
        document.body.classList.remove('second_level_actived');
        document.querySelectorAll('.menu-drawer__menu.submenu-open').forEach(ul => ul.classList.remove('submenu-open'));
      }
      return;
    }

    // 3) Close subpanel + drawer (X inside the panel header)
    const closeModalBtn = e.target.closest('.menu-drawer__close-modal-button');
    if (closeModalBtn) {
      e.preventDefault(); e.stopPropagation();
      document.querySelectorAll('details.menu-drawer__enhanced-megamenu[open]').forEach(d => {
        d.open = false;
        this.setExpanded(d.querySelector('summary'), false);
        d.classList.remove('menu-opening');
      });
      this.removeMainClass();

      // also close the main drawer
      const headerDrawer = closeModalBtn.closest('header-drawer');
      const mainDetails = headerDrawer?.querySelector('details.menu-drawer-container') || document.querySelector('header-drawer details.menu-drawer-container');
      const mainSummary = mainDetails?.querySelector('summary');

      if (headerDrawer && typeof headerDrawer.closeMenuDrawer === 'function') {
        headerDrawer.closeMenuDrawer(new Event('click'), mainSummary);
      } else if (mainDetails) {
        mainDetails.open = false;
        mainDetails.classList.remove('menu-opening');
        this.setExpanded(mainSummary, false);
      }
      return;
    }

    // 4) Mobile close (top bar button in the drawer header)
    const mobileClose = e.target.closest('.menu-drawer__close-button-mobile');
    if (mobileClose) {
      e.preventDefault(); e.stopPropagation();

      // Close any open enhanced megamenus and reset their ARIA
      document.querySelectorAll('details.menu-drawer__enhanced-megamenu[open]').forEach(d => {
        d.open = false;
        this.setExpanded(d.querySelector('summary'), false);
        d.classList.remove('menu-opening');
      });

      // Clear slide/overlay state + body flags
      document.body.classList.remove('second_level_actived');
      document.querySelectorAll('.menu-drawer__menu.submenu-open').forEach(el => el.classList.remove('submenu-open'));
      this.removeMainClass();

      // Close the main drawer and flip its ARIA
      const headerDrawer = mobileClose.closest('header-drawer') || document.querySelector('header-drawer');
      const mainDetails = headerDrawer?.querySelector('details.menu-drawer-container') || document.querySelector('header-drawer details.menu-drawer-container');
      const mainSummary = mainDetails?.querySelector('summary');

      if (headerDrawer && typeof headerDrawer.closeMenuDrawer === 'function') {
        headerDrawer.closeMenuDrawer(new Event('click'), mainSummary);
      } else if (mainDetails) {
        mainDetails.open = false;
        mainDetails.classList.remove('menu-opening');
        this.setExpanded(mainSummary, false);
      }

      return;
    }
  }

  // ---------------------------------------------------------------------------
  // helpers
  addMainClass() {
    const main = document.querySelector('main');
    if (main) main.classList.add('enhanced-megamenu-active');
    document.body.classList.add('enhanced-megamenu-active');
  }
  removeMainClass() {
    const main = document.querySelector('main');
    if (main) main.classList.remove('enhanced-megamenu-active');
    document.body.classList.remove('enhanced-megamenu-active');
  }
}

// ---- Single-shot init -------------------------------------------------------
function initEnhancedMegamenuDrawer() {
  if (window.__enhancedMegaInit) return;
  window.__enhancedMegaInit = true;
  window.__enhancedMega = new EnhancedMegamenuDrawer();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initEnhancedMegamenuDrawer, { once: true });
} else {
  initEnhancedMegamenuDrawer();
}
document.addEventListener('shopify:section:load', () => window.__enhancedMega?.refresh());
